import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Clock, MapPin, LogIn, LogOut, AlertCircle, CheckCircle, Loader2, History, PenTool } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import SignaturePad from '../components/SignaturePad';

export default function TimeClockPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [message, setMessage] = useState(null);
  const [records, setRecords] = useState([]);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signatureAction, setSignatureAction] = useState(null); // 'clock-in' or 'clock-out'
  const [pendingLocation, setPendingLocation] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await API.get('/clock-status');
      setStatus(response.data);
    } catch (err) {
      console.error('Error fetching status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const response = await API.get(`/time-records?start_date=${startDate}&end_date=${endDate}`);
      setRecords(response.data);
    } catch (err) {
      console.error('Error fetching records:', err);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchRecords();
  }, [fetchStatus, fetchRecords]);

  const getLocation = () => {
    return new Promise((resolve, reject) => {
      setGettingLocation(true);
      setLocationError(null);
      
      if (!navigator.geolocation) {
        setLocationError('La geolocalización no está disponible en tu navegador');
        setGettingLocation(false);
        reject(new Error('Geolocation not supported'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            address: null
          };
          
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.latitude}&lon=${coords.longitude}&zoom=18&addressdetails=1`,
              { headers: { 'Accept-Language': 'es' } }
            );
            const data = await response.json();
            if (data.display_name) {
              coords.address = data.display_name;
            }
          } catch (e) {
            console.log('Could not get address:', e);
          }
          
          setLocation(coords);
          setGettingLocation(false);
          resolve(coords);
        },
        (error) => {
          let errorMsg = 'Error al obtener ubicación';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Permiso de ubicación denegado. Debes permitir el acceso a tu ubicación.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Información de ubicación no disponible';
              break;
            case error.TIMEOUT:
              errorMsg = 'Tiempo de espera agotado al obtener ubicación';
              break;
            default:
              break;
          }
          setLocationError(errorMsg);
          setGettingLocation(false);
          reject(new Error(errorMsg));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0
        }
      );
    });
  };

  const initiateClockIn = async () => {
    setMessage(null);
    let coords = { latitude: 0, longitude: 0, address: 'Ubicacion no disponible' };
    try {
      coords = await getLocation();
    } catch (err) {
      // Continue without location
    }
    setPendingLocation(coords);
    setSignatureAction('clock-in');
    setSignatureDialogOpen(true);
  };

  const initiateClockOut = async () => {
    setMessage(null);
    let coords = { latitude: 0, longitude: 0, address: 'Ubicacion no disponible' };
    try {
      coords = await getLocation();
    } catch (err) {
      // Continue without location
    }
    setPendingLocation(coords);
    setSignatureAction('clock-out');
    setSignatureDialogOpen(true);
  };

  const handleSignatureComplete = async (signatureData) => {
    setSignatureDialogOpen(false);
    setActionLoading(true);
    
    try {
      const payload = {
        ...pendingLocation,
        signature: signatureData
      };
      
      if (signatureAction === 'clock-in') {
        const response = await API.post('/clock-in', payload);
        setMessage({ type: 'success', text: response.data.message });
      } else {
        const response = await API.post('/clock-out', payload);
        setMessage({ type: 'success', text: `${response.data.message}. Total: ${response.data.total_hours}h` });
      }
      
      fetchStatus();
      fetchRecords();
    } catch (err) {
      setMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Error al registrar fichaje'
      });
    } finally {
      setActionLoading(false);
      setPendingLocation(null);
      setSignatureAction(null);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const formatLocation = (loc) => {
    if (!loc) return '-';
    if (loc.address) {
      const parts = loc.address.split(',');
      return parts.slice(0, 3).join(', ');
    }
    return `${loc.latitude.toFixed(4)}, ${loc.longitude.toFixed(4)}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const isClockedIn = status?.is_clocked_in;

  return (
    <div className="space-y-6 sm:space-y-8 max-w-4xl mx-auto" data-testid="time-clock-page">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-gray-900" >
          Control Horario
        </h1>
        <p className="text-gray-500 mt-1 text-sm sm:text-base">Ficha tu entrada y salida con ubicación y firma</p>
      </div>

      {/* Current Status Card */}
      <div className="bg-white border border-orange-100 rounded-lg p-6 sm:p-8">
        <div className="text-center space-y-6">
          {/* Clock Icon with Status */}
          <div className={`inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full ${isClockedIn ? 'bg-orange-100' : 'bg-gray-100'}`}>
            <Clock className={`w-10 h-10 sm:w-12 sm:h-12 ${isClockedIn ? 'text-orange-500' : 'text-gray-400'}`} />
          </div>

          {/* Status Text */}
          <div>
            <Badge className={`text-sm px-4 py-1 ${isClockedIn ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>
              {isClockedIn ? 'TRABAJANDO' : 'SIN FICHAR'}
            </Badge>
            {isClockedIn && status?.current_record && (
              <p className="mt-2 text-sm text-gray-500">
                Entrada: {formatTime(status.current_record.clock_in_time)}
              </p>
            )}
            {status?.total_hours_today > 0 && (
              <p className="mt-1 text-sm text-gray-500">
                Horas hoy: <span className="font-semibold text-gray-900">{status.total_hours_today}h</span>
              </p>
            )}
          </div>

          {/* Message */}
          {message && (
            <div className={`flex items-center justify-center gap-2 p-3 rounded-md ${
              message.type === 'success' ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'
            }`}>
              {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span className="text-sm">{message.text}</span>
            </div>
          )}

          {/* Location Info */}
          {gettingLocation && (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              Obteniendo ubicación...
            </div>
          )}

          {locationError && (
            <div className="flex items-center justify-center gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4" />
              {locationError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isClockedIn ? (
              <Button
                onClick={initiateClockIn}
                disabled={actionLoading || gettingLocation}
                className="h-14 sm:h-16 px-8 sm:px-12 text-lg bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-md shadow-orange-200 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                data-testid="clock-action-btn"
              >
                {(actionLoading || gettingLocation) ? (
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                ) : (
                  <LogIn className="w-6 h-6 mr-2" />
                )}
                FICHAR ENTRADA
              </Button>
            ) : (
              <Button
                onClick={initiateClockOut}
                disabled={actionLoading || gettingLocation}
                className="h-14 sm:h-16 px-8 sm:px-12 text-lg bg-red-600 hover:bg-red-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all"
                data-testid="clock-out-btn"
              >
                {(actionLoading || gettingLocation) ? (
                  <Loader2 className="w-6 h-6 animate-spin mr-2" />
                ) : (
                  <LogOut className="w-6 h-6 mr-2" />
                )}
                FICHAR SALIDA
              </Button>
            )}
          </div>

          {/* Info Note */}
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <MapPin className="w-3 h-3" />
            <span>Ubicación GPS</span>
            <span className="mx-1">+</span>
            <PenTool className="w-3 h-3" />
            <span>Firma digital</span>
          </div>
        </div>
      </div>

      {/* Recent Records */}
      <div className="bg-white border border-orange-100 rounded-lg">
        <div className="p-4 sm:p-6 border-b border-orange-100 flex items-center gap-2">
          <History className="w-5 h-5 text-gray-400" />
          <h2 className="text-lg font-bold text-gray-900" >
            Últimos 7 días
          </h2>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-orange-100 bg-[#FFF8F4]">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Fecha</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Entrada</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 hidden sm:table-cell">Ubicación</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Salida</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Horas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No hay registros de fichaje
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record.id} className="hover:bg-orange-50/50" data-testid={`record-row-${record.id}`}>
                    <TableCell className="font-medium text-gray-900">{formatDate(record.clock_in_time)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-600 font-mono">{formatTime(record.clock_in_time)}</span>
                        {record.clock_in_signature && <PenTool className="w-3 h-3 text-orange-500" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-500 text-sm hidden sm:table-cell max-w-[200px] truncate">
                      {formatLocation(record.clock_in_location)}
                    </TableCell>
                    <TableCell>
                      {record.clock_out_time ? (
                        <div className="flex items-center gap-1">
                          <span className="text-gray-600 font-mono">{formatTime(record.clock_out_time)}</span>
                          {record.clock_out_signature && <PenTool className="w-3 h-3 text-orange-500" />}
                        </div>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">En curso</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-gray-900">
                      {record.total_hours ? `${record.total_hours}h` : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Signature Dialog */}
      <Dialog open={signatureDialogOpen} onOpenChange={setSignatureDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenTool className="w-5 h-5" />
              {signatureAction === 'clock-in' ? 'Firma de Entrada' : 'Firma de Salida'}
            </DialogTitle>
            <DialogDescription>
              Firma para confirmar tu {signatureAction === 'clock-in' ? 'entrada' : 'salida'}.
              {pendingLocation?.address && (
                <span className="block mt-2 text-xs">
                  <MapPin className="w-3 h-3 inline mr-1" />
                  {pendingLocation.address.split(',').slice(0, 2).join(', ')}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <SignaturePad
            onSave={handleSignatureComplete}
            onCancel={() => {
              setSignatureDialogOpen(false);
              setPendingLocation(null);
              setSignatureAction(null);
            }}
            title="Dibuja tu firma"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
